import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  if (user) return <Navigate to="/pipeline" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/pipeline");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "This email is not authorised to access CompeteWiser."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex"
      style={{ background: "var(--cw-primary-dark)" }}
      data-testid="login-page"
    >
      {/* Left brand panel */}
<div className="hidden md:flex flex-col justify-between p-14 w-1/2 relative overflow-hidden bg-[#F8FAFC]">
<div className="flex items-center gap-4">
  <div className="w-1/4 flex items-center justify-center">
    <img src="/assets/logo.png" alt="CompeteWiser logo" className="max-w-full h-auto" />
  </div>
  <div className="w-3/4">
    <div className="font-['Plus_Jakarta_Sans',sans-serif] text-[#0F172A] text-4xl font-bold tracking-tight">
      CompeteWiser
    </div>
    <div className="mt-2 text-slate-500 text-sm">
      The VC Operating System
    </div>
  </div>
</div>
  <div className="relative z-10">
    <h1 className="font-['Plus_Jakarta_Sans',sans-serif] text-[#0F172A] text-[36px] font-bold leading-tight max-w-lg">
      AI Powered <br />Operating System<br />
      One workflow configured around you
    </h1>
    <p className="mt-6 text-slate-500 max-w-md text-sm leading-relaxed">
      From Deal Screening, Funnel management to IC decisions and beyond
    </p>
  </div>
  <div className="text-xs text-slate-400">
    © {new Date().getFullYear()} ProdZen · hello@theprodzen.com
  </div>
  {/* Decorative accent */}
  <div
    className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full"
    style={{ background: "var(--cw-primary)", opacity: 0.2, filter: "blur(60px)" }}
  />
  <div
    className="absolute top-1/3 -right-32 w-72 h-72 rounded-full"
    style={{ background: "var(--cw-secondary)", opacity: 0.12, filter: "blur(80px)" }}
  />
</div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#0F172A]">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div>
            <div className="cw-caption uppercase tracking-widest">Log in</div>
            <h1 className="cw-h1 mt-1">Welcome back</h1>
            <p className="text-sm text-[var(--cw-muted)] mt-2">
              Use your CompeteWiser workspace credentials.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="cw-caption block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-11 px-3 rounded-md border bg-white outline-none"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="login-email-input"
                placeholder="you@vcfirm.com"
              />
            </div>
            <div>
              <label className="cw-caption block mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-11 px-3 rounded-md border bg-white outline-none"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="cw-btn-primary w-full h-11 flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            <LogIn size={16} />
            {submitting ? "Signing in…" : "Log in"}
          </button>

          {/* v6.2 — Forgot password (self-service page replaces the old modal) */}
          <button
            type="button"
            onClick={() => { window.location.href = "/forgot-password"; }}
            className="w-full text-center text-xs mt-2"
            style={{ color: "var(--cw-muted)" }}
            data-testid="forgot-password-link"
          >
            Forgot your password?
          </button>

         
        </form>
      </div>
      {showForgot && <ForgotPasswordModal defaultEmail={email} onClose={() => setShowForgot(false)} />}
    </div>
  );
}

function ForgotPasswordModal({ defaultEmail, onClose }) {
  const [emailF, setEmailF] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: emailF.trim().toLowerCase() });
      setMsg(data.message || "Request submitted.");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" data-testid="forgot-password-modal">
        <h3 className="cw-h3 mb-2">Forgot password</h3>
        {done ? (
          <div className="text-sm text-[var(--cw-body)]" data-testid="forgot-success">
            {msg}
            <div className="mt-4"><button className="cw-btn-secondary" onClick={onClose}>Close</button></div>
          </div>
        ) : (
          <>
            <p className="text-xs text-[var(--cw-muted)] mb-3">
              Your VC Admin will review your request and share a reset link with you outside the app.
            </p>
            <input value={emailF} onChange={(e) => setEmailF(e.target.value)} placeholder="you@vcfirm.com"
              className="w-full h-10 px-3 rounded-md border bg-white outline-none text-sm"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="forgot-email-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="cw-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="cw-btn-primary" onClick={submit} disabled={busy || !emailF.trim()}
                data-testid="forgot-submit-btn">
                {busy ? "Sending…" : "Request reset"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
